import { YEAR_SECONDS, readAnalyticsCookie, writeAnalyticsCookie } from './analytics-cookie';
import { clearAnalyticsSession, forgetAnalyticsVisitor } from './analytics-session';

/**
 * Whether this browser sends product analytics (`docs/product-analytics.md`).
 *
 * The choice is a cookie both hosts of the site read (`analytics-cookie.ts`), so turning it off on
 * the library turns it off on the creator dashboard too. The desktop app's window keeps cookies of
 * its own, apart from any browser's, so its choice is made in the window (`AnalyticsChoice.tsx`).
 * The cookie is `off` or `on`, and once it exists it is the answer.
 *
 * Before the cookie the choice was this origin's `localStorage`, and it still is a copy there: an
 * earlier `off` found with no cookie is carried into one — so a choice made on either host before
 * now reaches both — and a browser that will not keep the cookie keeps the choice per origin, as
 * it always did.
 */

const OPT_OUT = 'boardflight.analytics.off';
const CHOICE = 'bf_analytics';
export const ANALYTICS_PREFERENCE_EVENT = 'boardflight-analytics-preference';

/** The cookie's value when this page last copied it to `localStorage`, so that happens once per value. */
let copied: string | null = null;

export function browserDeclinesAnalytics(): boolean {
  return typeof navigator !== 'undefined' &&
    (navigator.doNotTrack === '1' || ('globalPrivacyControl' in navigator && navigator.globalPrivacyControl === true));
}

export function analyticsOptedOut(): boolean {
  if (browserDeclinesAnalytics()) return true;
  const choice = readAnalyticsCookie(CHOICE);
  if (choice === 'off' || choice === 'on') {
    if (choice !== copied) {
      copied = choice;
      // Renewed once a page load, so a choice lasts a year from the last visit; and copied, so an
      // older `off` in this origin's storage cannot come back if the cookie ever lapses.
      writeAnalyticsCookie(CHOICE, choice, YEAR_SECONDS);
      try {
        if (choice === 'off') localStorage.setItem(OPT_OUT, '1');
        else localStorage.removeItem(OPT_OUT);
      } catch { /* The cookie is the choice. */ }
    }
    return choice === 'off';
  }
  try {
    const off = localStorage.getItem(OPT_OUT) === '1';
    if (off) writeAnalyticsCookie(CHOICE, 'off', YEAR_SECONDS);
    return off;
  } catch { return true; }
}

export function setAnalyticsOptOut(off: boolean): void {
  const choice = off ? 'off' : 'on';
  if (writeAnalyticsCookie(CHOICE, choice, YEAR_SECONDS)) copied = choice;
  try {
    if (off) localStorage.setItem(OPT_OUT, '1');
    else localStorage.removeItem(OPT_OUT);
  } catch { /* Unavailable storage already disables capture. */ }
  // A change of mind ends the anonymous identity, too, on both hosts.
  forgetAnalyticsVisitor();
  clearAnalyticsSession();
  window.dispatchEvent(new Event(ANALYTICS_PREFERENCE_EVENT));
}

/** Carry the browser's choice to the API so successful actions respect it as well. */
export function analyticsHeaders(headers: Headers): Headers {
  if (analyticsOptedOut()) headers.set('x-boardflight-analytics', 'off');
  return headers;
}
